<groundworks-card [isCollapsible]="true" [constrained]="true" constrainedSize="30">
  <groundworks-card-header>
    <ng-template groundworksCardHeaderTitle>
      <div class="d-flex align-items-center gap-2">
        <h2 class="card-title h4 m-0">{{ headerTitle() }}</h2>
        <!-- Cancelled/descoped work still renders its worked install days, so the header has to say
             why the dates are locked and why no new one can be added. Matches the project-header
             badge. Cancellation wins when a project is somehow both — see isCurrentProjectLocked. -->
        @if (isCurrentProjectCancelled()) {
          <groundworks-badge data-testid="panel-cancelled-badge"
                             label="Cancelled" type="danger" icon="bi bi-x-circle" size="large"/>
        } @else if (isCurrentProjectDescoped()) {
          <groundworks-badge data-testid="panel-descoped-badge"
                             label="Descoped" type="indigo" icon="bi bi-x-circle" size="large"/>
        }
      </div>
    </ng-template>
  </groundworks-card-header>
  <groundworks-card-body>
    @if (showLoading()) {
      <groundworks-state-message type="loading" message="Loading project details..." />
    }

    @if (hasError() && !showLoading()) {
      <groundworks-state-message type="error"
        message="Failed to load project details. Please try again." [button]="retryButton" />
    }

    @if (saveError()?.banner; as banner) {
      <div class="d-flex align-items-start gap-2 p-3 rounded border mb-3"
           style="background-color: #F8D7DA; color: #842029;"
           data-testid="save-error-banner">
        <i class="bi bi-x-circle-fill fs-5 mt-1"></i>
        <span>{{ banner }}</span>
      </div>
    }

    @if (showContent()) {
      @if (projects().length > 1) {
        <ul class="nav nav-tabs mb-3">
          @for (project of projects(); track project.id; let i = $index) {
            <li class="nav-item">
              <button type="button"
                      class="nav-link"
                      data-testid="project-tab"
                      [class.active]="i === selectedProjectIndex()"
                      [disabled]="isSaving() || (hasUnsavedChanges() && !form.valid)"
                      (click)="selectProject(i)">
                {{ project.projectTypeName || 'Project' }} {{ i + 1 }}/{{ projects().length }}
              </button>
            </li>
          }
        </ul>
      }

      <div class="row g-4">
        <div class="col-12 col-lg-5 d-flex flex-column gap-4">
          @if (customerData(); as data) {
            <groundworks-card [cardClasses]="['shadow-none']">
              <groundworks-card-header>
                <ng-template groundworksCardHeaderTitle>
                  <h5 class="card-title h5 m-0">Customer Information</h5>
                </ng-template>
              </groundworks-card-header>
              <groundworks-card-body>
                <groundworks-customer-details-card [customerData]="data" [muted]="true" />
              </groundworks-card-body>
            </groundworks-card>
          }

          @if (project()?.boundProducts?.length) {
            <groundworks-card [cardClasses]="['shadow-none']">
              <groundworks-card-header>
                <ng-template groundworksCardHeaderTitle>
                  <h5 class="card-title h5 m-0">Products</h5>
                </ng-template>
              </groundworks-card-header>
              <groundworks-card-body>
                <groundworks-table [data]="productsTableData()" />
              </groundworks-card-body>
            </groundworks-card>
          }
        </div>

        <div class="col-12 col-lg-7 d-flex flex-column">
        <groundworks-card [cardClasses]="['shadow-none']" [fill]="true">
          <groundworks-card-header>
            <ng-template groundworksCardHeaderTitle>
              <h5 class="card-title h5 m-0">Install Dates</h5>
            </ng-template>
          </groundworks-card-header>
          <groundworks-card-body [fill]="true" [cardBodyClasses]="['px-4', 'd-flex', 'flex-column']">
            @if (visibleRows().length === 0) {
              <groundworks-state-message
                class="flex-grow-1 d-flex"
                type="initial"
                message="Select the date(s) on the calendar you would like to add and configure."
                customIcon="bi bi-calendar" />
            } @else {
              <form [formGroup]="form">
                <fieldset>
                  <div formArrayName="installDates">
                    @for (row of visibleRows(); track row.control) {
                      <div class="install-date-row-grid mb-2"
                           [formGroupName]="row.index">

                        <div class="d-inline-flex align-items-center gap-2"
                             [ngbTooltip]="configService.enableCrew ? rowLockTooltip(row.index) : ''" placement="top" container="body">
                          <span class="text-nowrap d-inline-flex align-items-center gap-1">
                            {{ row.control.get('date')?.value | date:'MM/dd/yyyy':'UTC' }}
                            @if (!isCurrentProjectCsvMode()) {
                              @if (canRemoveInstallDateRow(row.index)) {
                                <button type="button"
                                        class="btn btn-sm p-0 border-0"
                                        (click)="removeRow(row.index)"
                                        aria-label="Remove date">
                                  <i class="bi bi-trash text-danger"></i>
                                </button>
                              } @else {
                                <span class="btn btn-sm p-0 border-0 invisible">
                                  <i class="bi bi-trash"></i>
                                </span>
                              }
                            }
                          </span>
                          <div class="form-check form-switch d-flex align-items-center gap-2 mb-0">
                            <input class="form-check-input"
                                   type="checkbox"
                                   role="switch"
                                   formControlName="isAllDay"
                                   [id]="'panel-all-day-toggle-' + row.index" />
                            <label class="form-check-label text-nowrap"
                                   [for]="'panel-all-day-toggle-' + row.index">All Day</label>
                          </div>
                        </div>

                        <div class="d-flex align-items-center gap-2"
                             [ngbTooltip]="configService.enableCrew ? rowLockTooltip(row.index) : ''" placement="top" container="body">
                          <span class="text-nowrap fw-semibold">F:</span>
                          <ng-select
                            class="flex-grow-1"
                            style="min-width: 0"
                            [items]="foremanOptionsForRow(row.index)"
                            bindLabel="name"
                            bindValue="id"
                            groupBy="group"
                            placeholder="No foreman"
                            formControlName="foremanUserId"
                            (change)="onForemanChange(row.control.get('foremanUserId')?.value, row.index)"
                            [clearable]="false"
                            appendTo="body"
                            [attr.data-testid]="'foreman-select-' + row.index"
                          />

                          @if (configService.enableCrew) {
                            <div class="input-group" style="width: 110px">
                              <input type="number"
                                     class="form-control"
                                     formControlName="foremanAllocationPercentage"
                                     placeholder=""
                                     min="0"
                                     max="100"
                                     (input)="clampPercentage(row.control.get('foremanAllocationPercentage')!, $event)"
                                     [attr.data-testid]="'foreman-allocation-' + row.index" />
                              <span class="input-group-text">%</span>
                            </div>
                            <!-- Adds the first crew member. Rows are built with no crew picker at all, so
                                 an empty "C:" select always means a real unsaved pick and never a
                                 placeholder — a blank one is a signal, not noise. Once a crew row exists it
                                 carries its own +, so this one hides. The invisible trash keeps the + in the
                                 same column as the crew rows' +.
                                 Cancellation does NOT hide this: crew stays editable on cancelled rows so
                                 worked labour can be attributed. isInstallDateRowCrewLocked is the only gate. -->
                            <div class="d-flex gap-1" style="width: 46px; flex-shrink: 0">
                              @if (getCrewAllocationsArray(row.index).length === 0 && !isInstallDateRowCrewLocked(row.index)) {
                                <span class="btn btn-sm p-0 border-0 invisible">
                                  <i class="bi bi-trash"></i>
                                </span>
                                <button type="button"
                                        class="btn btn-sm p-0 border-0"
                                        (click)="addCrewMember(row.index)"
                                        aria-label="Add crew member"
                                        [attr.data-testid]="'foreman-add-crew-' + row.index">
                                  <i class="bi bi-plus-circle-fill text-secondary"></i>
                                </button>
                              }
                            </div>
                          }

                        </div>

                        @if (configService.enableCrew) {
                          <ng-container formArrayName="crewAllocations">
                            @for (crew of getCrewAllocationsArray(row.index).controls; track crew; let ci = $index) {
                              <div class="install-date-row-spacer"></div>
                              <div class="d-flex align-items-center gap-2" [formGroupName]="ci"
                                   [ngbTooltip]="crewLockTooltip(row.index)" placement="top" container="body">
                                <span class="fw-semibold text-nowrap">C:</span>
                                <ng-select
                                  class="flex-grow-1"
                                  style="min-width: 0"
                                  [items]="crewOptionsForForeman(row.control.get('foremanUserId')?.value)"
                                  bindLabel="name"
                                  bindValue="userId"
                                  placeholder="Select crew member"
                                  formControlName="userId"
                                  [loading]="isCrewLoadingForForeman(row.control.get('foremanUserId')?.value)"
                                  [clearable]="false"
                                  appendTo="body"
                                  [attr.data-testid]="'crew-select-' + row.index + '-' + ci"
                                />
                                <div class="input-group" style="width: 110px">
                                  <input type="number"
                                         class="form-control"
                                         formControlName="allocationPercentage"
                                         placeholder=""
                                         min="0"
                                         max="100"
                                         (input)="clampPercentage(crew.get('allocationPercentage')!, $event)"
                                         [attr.data-testid]="'crew-allocation-' + row.index + '-' + ci" />
                                  <span class="input-group-text">%</span>
                                </div>
                                <div class="d-flex gap-1" style="width: 46px; flex-shrink: 0">
                                  @if (!isInstallDateRowCrewLocked(row.index)) {
                                    <!-- Enabled even with no selection: the + creates an empty picker, so
                                         the trash has to be able to take it back away. Cancellation is not
                                         a gate here — see the foreman-row + above. -->
                                    <button type="button"
                                            class="btn btn-sm p-0 border-0"
                                            (click)="removeCrewMember(row.index, ci)"
                                            aria-label="Remove crew member">
                                      <i class="bi bi-trash text-danger"></i>
                                    </button>
                                    <button type="button"
                                            class="btn btn-sm p-0 border-0"
                                            (click)="addCrewMember(row.index)"
                                            aria-label="Add crew member">
                                      <i class="bi bi-plus-circle-fill text-secondary"></i>
                                    </button>
                                  }
                                </div>
                              </div>
                            }
                          </ng-container>
                        }

                        @if (!row.control.get('isAllDay')?.value) {
                          <div class="install-date-row-spacer"></div>
                          <div class="d-flex align-items-center gap-2 mb-1"
                               [ngbTooltip]="configService.enableCrew ? rowLockTooltip(row.index) : ''" placement="top" container="body">
                            <span class="text-nowrap">From:</span>
                            <input type="time"
                                   class="form-control form-control-sm w-auto"
                                   formControlName="startTime" />
                            <span class="text-nowrap">to:</span>
                            <input type="time"
                                   class="form-control form-control-sm w-auto"
                                   formControlName="endTime" />
                          </div>
                        }
                      </div>
                      @if (!isCurrentProjectCsvMode() && saveError()?.rowErrors?.[row.index]; as rowMsg) {
                        <div class="alert alert-danger row-error mt-2"
                             role="alert"
                             [attr.data-testid]="'row-save-error-' + row.index">
                          {{ rowMsg }}
                        </div>
                      }
                      @if (!isCurrentProjectCsvMode() && saveAttempted() && !saveError()?.rowErrors?.[row.index] && rowAvailabilityError(row.index); as availErr) {
                        <div class="alert alert-danger row-error mt-2"
                             role="alert"
                             [attr.data-testid]="'row-availability-error-' + row.index">
                          {{ availErr }}
                        </div>
                      }
                    }
                  </div>
                </fieldset>
              </form>
            }
          </groundworks-card-body>
        </groundworks-card>

        @if (isCurrentProjectCsvMode()) {
          <groundworks-card [cardClasses]="['shadow-none', 'mt-4']" [fill]="true">
            <groundworks-card-header>
              <ng-template groundworksCardHeaderTitle>
                <h5 class="card-title h5 m-0">Customer Satisfaction Visits</h5>
              </ng-template>
            </groundworks-card-header>
            <groundworks-card-body [fill]="true" [cardBodyClasses]="['px-4', 'd-flex', 'flex-column']">
              @if (visibleCsvRows().length === 0) {
                <groundworks-state-message
                  class="flex-grow-1 d-flex"
                  type="initial"
                  message="Select the date(s) on the calendar you would like to add and configure."
                  customIcon="bi bi-calendar" />
              } @else {
                <form [formGroup]="form">
                  <div formArrayName="customerSatisfactionVisits">
                    @for (row of visibleCsvRows(); track row.control) {
                      <div class="install-date-row-grid mb-2"
                           [formGroupName]="row.index">

                        <div class="d-inline-flex align-items-center gap-2"
                             [ngbTooltip]="configService.enableCrew ? csvRowLockTooltip(row.index) : ''" placement="top" container="body">
                          <span class="text-nowrap d-inline-flex align-items-center gap-1">
                            {{ row.control.get('date')?.value | date:'MM/dd/yyyy':'UTC' }}
                            @if (canRemoveCsvRow(row.index)) {
                              <button type="button"
                                      class="btn btn-sm p-0 border-0"
                                      (click)="removeCsvRow(row.index)"
                                      aria-label="Remove visit">
                                <i class="bi bi-trash text-danger"></i>
                              </button>
                            } @else {
                              <span class="btn btn-sm p-0 border-0 invisible">
                                <i class="bi bi-trash"></i>
                              </span>
                            }
                          </span>
                          <div class="form-check form-switch d-flex align-items-center gap-2 mb-0">
                            <input class="form-check-input"
                                   type="checkbox"
                                   role="switch"
                                   formControlName="isAllDay"
                                   [id]="'panel-csv-all-day-toggle-' + row.index" />
                            <label class="form-check-label text-nowrap"
                                   [for]="'panel-csv-all-day-toggle-' + row.index">All Day</label>
                          </div>
                        </div>

                        <div class="d-flex align-items-center gap-2"
                             [ngbTooltip]="configService.enableCrew ? csvRowLockTooltip(row.index) : ''" placement="top" container="body">
                          <span class="text-nowrap fw-semibold">F:</span>
                          <ng-select
                            class="flex-grow-1"
                            style="min-width: 0"
                            [items]="foremanOptionsForCsvRow(row.index)"
                            bindLabel="name"
                            bindValue="id"
                            groupBy="group"
                            placeholder="No foreman"
                            formControlName="foremanUserId"
                            (change)="onCsvForemanChange(row.control.get('foremanUserId')?.value, row.index)"
                            [clearable]="false"
                            appendTo="body"
                            [attr.data-testid]="'csv-foreman-select-' + row.index"
                          />

                          @if (configService.enableCrew) {
                            <div class="input-group" style="width: 110px">
                              <input type="number"
                                     class="form-control"
                                     formControlName="foremanAllocationPercentage"
                                     placeholder=""
                                     min="0"
                                     max="100"
                                     (input)="clampPercentage(row.control.get('foremanAllocationPercentage')!, $event)"
                                     [attr.data-testid]="'csv-foreman-allocation-' + row.index" />
                              <span class="input-group-text">%</span>
                            </div>
                            <!-- See the install-date row: + only while the row has no crew, and
                                 cancellation is not a gate. -->
                            <div class="d-flex gap-1" style="width: 46px; flex-shrink: 0">
                              @if (getCsvCrewAllocationsArray(row.index).length === 0 && !isCsvRowCrewLocked(row.index)) {
                                <span class="btn btn-sm p-0 border-0 invisible">
                                  <i class="bi bi-trash"></i>
                                </span>
                                <button type="button"
                                        class="btn btn-sm p-0 border-0"
                                        (click)="addCsvCrewMember(row.index)"
                                        aria-label="Add crew member"
                                        [attr.data-testid]="'csv-foreman-add-crew-' + row.index">
                                  <i class="bi bi-plus-circle-fill text-secondary"></i>
                                </button>
                              }
                            </div>
                          }

                        </div>

                        @if (configService.enableCrew) {
                          <ng-container formArrayName="crewAllocations">
                            @for (crew of getCsvCrewAllocationsArray(row.index).controls; track crew; let ci = $index) {
                              <div class="install-date-row-spacer"></div>
                              <div class="d-flex align-items-center gap-2" [formGroupName]="ci"
                                   [ngbTooltip]="csvCrewLockTooltip(row.index)" placement="top" container="body">
                                <span class="fw-semibold text-nowrap">C:</span>
                                <ng-select
                                  class="flex-grow-1"
                                  style="min-width: 0"
                                  [items]="crewOptionsForForeman(row.control.get('foremanUserId')?.value)"
                                  bindLabel="name"
                                  bindValue="userId"
                                  placeholder="Select crew member"
                                  formControlName="userId"
                                  [loading]="isCrewLoadingForForeman(row.control.get('foremanUserId')?.value)"
                                  [clearable]="false"
                                  appendTo="body"
                                  [attr.data-testid]="'csv-crew-select-' + row.index + '-' + ci"
                                />
                                <div class="input-group" style="width: 110px">
                                  <input type="number"
                                         class="form-control"
                                         formControlName="allocationPercentage"
                                         placeholder=""
                                         min="0"
                                         max="100"
                                         (input)="clampPercentage(crew.get('allocationPercentage')!, $event)"
                                         [attr.data-testid]="'csv-crew-allocation-' + row.index + '-' + ci" />
                                  <span class="input-group-text">%</span>
                                </div>
                                <div class="d-flex gap-1" style="width: 46px; flex-shrink: 0">
                                  @if (!isCsvRowCrewLocked(row.index)) {
                                    <!-- See the install-date row: removable even when blank. -->
                                    <button type="button"
                                            class="btn btn-sm p-0 border-0"
                                            (click)="removeCsvCrewMember(row.index, ci)"
                                            aria-label="Remove crew member">
                                      <i class="bi bi-trash text-danger"></i>
                                    </button>
                                    <button type="button"
                                            class="btn btn-sm p-0 border-0"
                                            (click)="addCsvCrewMember(row.index)"
                                            aria-label="Add crew member">
                                      <i class="bi bi-plus-circle-fill text-secondary"></i>
                                    </button>
                                  }
                                </div>
                              </div>
                            }
                          </ng-container>
                        }

                        @if (!row.control.get('isAllDay')?.value) {
                          <div class="install-date-row-spacer"></div>
                          <div class="d-flex align-items-center gap-2 mb-1"
                               [ngbTooltip]="configService.enableCrew ? csvRowLockTooltip(row.index) : ''" placement="top" container="body">
                            <span class="text-nowrap">From:</span>
                            <input type="time"
                                   class="form-control form-control-sm w-auto"
                                   formControlName="startTime" />
                            <span class="text-nowrap">to:</span>
                            <input type="time"
                                   class="form-control form-control-sm w-auto"
                                   formControlName="endTime" />
                          </div>
                        }
                      </div>
                      @if (saveError()?.rowErrors?.[row.index]; as rowMsg) {
                        <div class="alert alert-danger row-error mt-2"
                             role="alert"
                             [attr.data-testid]="'csv-row-save-error-' + row.index">
                          {{ rowMsg }}
                        </div>
                      }
                      @if (saveAttempted() && !saveError()?.rowErrors?.[row.index] && rowAvailabilityError(row.index); as availErr) {
                        <div class="alert alert-danger row-error mt-2"
                             role="alert"
                             [attr.data-testid]="'csv-row-availability-error-' + row.index">
                          {{ availErr }}
                        </div>
                      }
                    }
                  </div>
                </form>
              }
            </groundworks-card-body>
          </groundworks-card>
        }
        </div>
      </div>
    }
  </groundworks-card-body>

  @if (showContent()) {
    <groundworks-card-actions [actions]="cardActions()" />
  }
</groundworks-card>
